<script>
  import { createPopper } from '@popperjs/core'
  import jump from 'jump.js'
  import sum from 'hash-sum'
  import { DEFAULT_STEP_OPTIONS } from '../shared/constants'

  export default {
    name: 'VStep',
    props: {
      step: {
        type: Object,
        default: () => {}
      },
      previousStep: {
        type: Function,
        required: false,
        default: () => {}
      },
      nextStep: {
        type: Function,
        required: false,
        default: () => {}
      },
      stop: {
        type: Function,
        required: false,
        default: () => {}
      },
      isFirst: {
        type: Boolean
      },
      isLast: {
        type: Boolean
      },
      labels: {
        type: Object,
        default: () => {}
      },
      skipable: {
        type: Boolean
      },
      activeClass: {
        type: String,
        default: ''
      }
    },

    data() {
      return {
        target: null,
        hash: sum(this.step.target),
        popper: null
      }
    },
    computed: {
      params() {
        return {
          ...DEFAULT_STEP_OPTIONS,
          ...this.step.params
        }
      },
      cardBindings() {
        return {
          id: `v-step-${this.hash}`,
          ref: `v-step-${this.hash}`,
          // maxWidth: 420,
          class: 'v-step',
          // color: 'accent'
          light: this.$vuetify.theme.dark,
          dark: !this.$vuetify.theme.dark
        }
      }
    },

    mounted() {
      this.$nextTick(() => {
        this.createStep()
      })
    },
    beforeDestroy() {
      if (this.target && this.activeClass) this.target.classList.remove(this.activeClass)
    },

    methods: {
      createStep() {
        const targetElement = document.querySelector(this.step.target)
        this.target = targetElement

        // TODO: debug mode
        // console.log('[Vue Tour] The target element ' + this.step.target + ' of .v-step[id="' + this.hash + '"] is:', targetElement)

        if (targetElement) {
          if (this.activeClass) targetElement.classList.add(this.activeClass)

          if (this.params.enableScrolling) {
            if (this.step.duration || this.step.offset) {
              const jumpOptions = {
                duration: this.step.duration || 1000,
                offset: this.step.offset || 0,
                callback: undefined,
                a11y: false
              }
              jump(targetElement, jumpOptions)
            }
            else {
              // Use the native scroll by default if no scroll options has been defined
              targetElement.scrollIntoView({ behavior: 'smooth' })
            }
          }

          /* eslint-disable no-new */
          this.popper = createPopper(
            targetElement,
            this.$refs['v-step-' + this.hash].$el,
            this.params
          )
          // TODO sometimes poppers positions are wrong (after update)
          // console.log('this.popper', this.popper)
          // window.popper = this.popper
        }
        else {
          console.error('[Vue Tour] The target element ' + this.step.target + ' of .v-step[id="' + this.hash + '"] does not exist!')
          this.$emit('targetNotFound', this.step)
        }
      }
    }
  }
</script>

<template>
  <VCard v-bind="cardBindings">
    <slot name="header">
      <VCardTitle v-if="step.header">
        <span class="title font-weight-bold">{{ step.header.title }}</span>
      </VCardTitle>
    </slot>

    <slot name="content">
      <VCardText v-if="step.content">{{ step.content }}</VCardText>
      <VCardText v-if="step.contents">
        <div v-for="(c, i) in step.contents" :key="i">{{ c }}</div>
      </VCardText>
    </slot>

    <slot name="actions">
      <VCardActions>
        <VBtn v-if="skipable && !isLast" text outlined small class="v--step__button" @click.left.prevent="stop">{{ labels.buttonSkip }}</VBtn>
        <VSpacer/>
        <VBtn v-if="!isFirst" text outlined small class="v--step__button" @click.left.prevent="previousStep">{{ labels.buttonPrevious }}</VBtn>
        <VBtn v-if="!isLast" text outlined small class="v--step__button" @click.left.prevent="nextStep">{{ labels.buttonNext }}</VBtn>
        <VBtn v-if="isLast" text outlined small class="v--step__button" @click.left.prevent="stop">{{ labels.buttonStop }}</VBtn>
      </VCardActions>
    </slot>

    <div class="v-step__arrow" data-popper-arrow></div>
  </VCard>
</template>

<style lang="scss" scoped>
  .v-step .v-step__arrow {
    position: absolute;
    width: 0;
    height: 0;
    margin: 0.5rem;
    border-color: inherit;
    border-style: solid;
  }

  .v-step[data-popper-placement^="top"] {
    margin-bottom: 0.5rem;
  }

  .v-step[data-popper-placement^="top"] .v-step__arrow {
    bottom: -0.5rem;
    left: calc(50% - 1rem);
    margin-top: 0;
    margin-bottom: 0;
    border-width: 0.5rem 0.5rem 0 0.5rem;
    border-right-color: transparent;
    border-bottom-color: transparent;
    border-left-color: transparent;
  }

  .v-step[data-popper-placement^="bottom"] {
    margin-top: 0.5rem;
  }

  .v-step[data-popper-placement^="bottom"] .v-step__arrow {
    top: -0.5rem;
    left: calc(50% - 1rem);
    margin-top: 0;
    margin-bottom: 0;
    border-width: 0 0.5rem 0.5rem 0.5rem;
    border-top-color: transparent;
    border-right-color: transparent;
    border-left-color: transparent;
  }

  .v-step[data-popper-placement^="right"] {
    margin-left: 0.5rem;
  }

  .v-step[data-popper-placement^="right"] .v-step__arrow {
    top: calc(50% - 1rem);
    left: -0.5rem;
    margin-right: 0;
    margin-left: 0;
    border-width: 0.5rem 0.5rem 0.5rem 0;
    border-top-color: transparent;
    border-bottom-color: transparent;
    border-left-color: transparent;
  }

  .v-step[data-popper-placement^="left"] {
    margin-right: 0.5rem;
  }

  .v-step[data-popper-placement^="left"] .v-step__arrow {
    top: calc(50% - 1rem);
    right: -0.5rem;
    margin-right: 0;
    margin-left: 0;
    border-width: 0.5rem 0 0.5rem 0.5rem;
    border-top-color: transparent;
    border-right-color: transparent;
    border-bottom-color: transparent;
  }
</style>

<style>
  .v-step--active {
    outline: 1px dashed rgba(154, 205, 50, 0.5);
  }
</style>
